// src/components/shared/Footer.jsx — Story Threads Vibe.
//
// Carried MindShield's name and description, and an "Also try Inner Voice"
// line, until September 2026. The legal links go to the Dive Deep Dialogues
// site: the in-app policy pages were Inner Voice's and were never routed, so
// the old links went nowhere.

import { ExternalLink, Heart } from 'lucide-react';
import { useApp } from '../../context/AppContext';

const FOOTER_LINKS = [
  {
    title: 'Also from Dive Deep Dialogues',
    links: [
      { name: 'Story Threads (adult edition)', href: 'https://storythreads.divedeepdialogues.com' },
      { name: 'MyStories FOREVER Vault', href: 'https://mystoriesforevervault.com' },
    ],
  },
  {
    title: 'Legal',
    links: [
      // In-app, and for everyone: guests keep everything on the device, so
      // they need the download and delete buttons more than anyone.
      { name: 'Your data', view: 'data-privacy' },
      { name: 'Privacy Policy', href: 'https://divedeepdialogues.com/privacy' },
      { name: 'Terms & Conditions', href: 'https://divedeepdialogues.com/terms' },
    ],
  },
  {
    title: 'About',
    links: [
      { name: 'Dive Deep Dialogues', href: 'https://divedeepdialogues.com', logo: true },
      { name: 'Contact', href: 'mailto:support@divedeepdialogues.com' },
    ],
  },
];

const linkClass =
  'text-sm text-slate-400 hover:text-thread-sand transition-colors inline-flex items-center gap-2 min-h-[44px]';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { setCurrentView } = useApp();

  return (
    <footer className="relative mt-auto">
      <div className="bg-thread-darker border-t border-thread-border">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 max-w-7xl">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <img src="/story-threads-vibe-logo-96.webp" alt="" width="40" height="40" loading="lazy" decoding="async" className="w-10 h-10 rounded-lg" />
                <div>
                  <h3 className="text-xl font-bold text-white">Story Threads Vibe</h3>
                  <p className="text-sm text-thread-sand">
                    A <span className="font-bold">Dive Deep Dialogues</span> app
                  </p>
                </div>
              </div>
              <p className="text-sm text-slate-400 leading-relaxed">Find, tell and keep your family&apos;s stories.</p>
            </div>

            {FOOTER_LINKS.map((section) => (
              <div key={section.title} className="space-y-4">
                <h4 className="text-sm font-semibold text-white uppercase tracking-wider">{section.title}</h4>
                <ul className="space-y-1">
                  {section.links.map((link) => {
                    if (link.view) {
                      return (
                        <li key={link.name}>
                          <button
                            type="button"
                            onClick={() => {
                              setCurrentView(link.view);
                              window.scrollTo(0, 0);
                            }}
                            className={linkClass}
                          >
                            {link.name}
                          </button>
                        </li>
                      );
                    }
                    const external = link.href.startsWith('http');
                    return (
                      <li key={link.name}>
                        <a
                          href={link.href}
                          {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                          className={linkClass}
                        >
                          {link.logo && (
                            <img
                              src="/ddd-logo-96.webp"
                              alt=""
                              width="48"
                              height="48"
                              loading="lazy"
                              decoding="async"
                              className="w-12 h-12"
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                              }}
                            />
                          )}
                          {link.name}
                          {external && <ExternalLink className="w-3 h-3" aria-hidden="true" />}
                        </a>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>

          <div className="mt-8 pt-6 border-t border-thread-border flex flex-col sm:flex-row justify-between items-center gap-4 text-sm text-slate-400">
            <p>© {currentYear} SILVER WOLF Productions. All rights reserved.</p>
            <p className="inline-flex items-center gap-1">
              Made with <Heart className="w-3.5 h-3.5 text-thread-glow fill-current" aria-hidden="true" /> by Dive Deep
              Dialogues
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
